import { gatewayCall } from '../../share/gateway';
import { uploadAudioReference, uploadVideoReference, uploadPicture } from '../../services/media';
import { makesVideo, startingVideoModel, videoPayload, videoRaw } from './videoModel';
import { readReferenceMedia } from '../../services/library';
import { getElement, elementThumbnail, resolveElements } from '../../services/elements';
import { promptForModel } from './promptMentions';
import { io } from 'socket.io-client';
import { currentTokenGetter } from '../../services/apiClient';
import type { GenerationClient, MediaKind, Model, Reference, Request, Result, Run, RunFeed, RunFrame } from './contracts';

const SOCKET_URL = import.meta.env.VITE_BACKEND_URL as string | undefined;

/**
 * Live run frames from the gateway's `/playground` namespace — the same socket
 * Flow's Playground listens on (usePlaygroundSocket). nodes-back emits
 * `standalone.job.*` for every standalone run, Artwork's included. The run's
 * own `generation:{id}` room is joined for each run still in flight, so a
 * collaborator on the file hears it too, and memberships are re-joined on every
 * connect (rooms are per connection). Frames are applied by EVENT NAME.
 */
function playgroundFeed(onFrame: (frame: RunFrame) => void, onConnection: (connected: boolean) => void): RunFeed {
  if (!SOCKET_URL) { onConnection(false); return { follow() {}, close() {} }; }
  const socket = io(`${SOCKET_URL}/playground`, {
    auth: async (cb: (data: object) => void) => {
      try { const token = await currentTokenGetter()?.(); cb(token ? { token } : {}); } catch { cb({}); }
    },
    transports: ['websocket'], reconnection: true, reconnectionAttempts: Infinity, reconnectionDelay: 1000, reconnectionDelayMax: 5000,
  });
  let wanted = new Set<string>(), joined = new Set<string>();
  const sweep = () => {
    if (!socket.connected) return;
    for (const id of wanted) if (!joined.has(id)) { joined.add(id); socket.emit('subscribe', { generationId: id }); }
    for (const id of joined) if (!wanted.has(id)) { joined.delete(id); socket.emit('unsubscribe', { generationId: id }); }
  };
  const idOf = (frame: Record<string, unknown>) => String(frame?.generationId || frame?.id || '');
  const on = (name: string, event: RunFrame['event']) => socket.on(name, (frame: Record<string, unknown>) => {
    const id = idOf(frame); if (!id) return;
    // A cancel is persisted as failed + CANCELLED; `progress` is a PERCENT and optional.
    onFrame({ id, event: event === 'failed' && frame.errorCode === 'CANCELLED' ? 'cancelled' : event,
      ...(typeof frame.progress === 'number' && Number.isFinite(frame.progress) ? { progress: frame.progress } : {}),
      ...(typeof frame.stage === 'string' ? { stage: frame.stage } : {}) });
  });
  on('playground:job:queued', 'queued'); on('playground:job:progress', 'progress'); on('playground:job:completed', 'completed');
  on('playground:job:failed', 'failed'); on('playground:job:cancelled', 'cancelled');
  socket.on('connect', () => { joined = new Set(); onConnection(true); sweep(); });
  socket.on('disconnect', () => onConnection(false));
  socket.on('connect_error', () => onConnection(false));
  return {
    follow(ids) { wanted = new Set(ids); sweep(); },
    close() { socket.removeAllListeners(); socket.disconnect(); },
  };
}

/** A clip's size and length from its metadata, without a CORS request. */
function measureClip(src: string): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const timer = setTimeout(() => { video.removeAttribute('src'); reject(Error('The video result could not be read yet.')); }, 20_000);
    video.preload = 'metadata'; video.muted = true;
    video.onloadedmetadata = () => { clearTimeout(timer); resolve({ width: video.videoWidth, height: video.videoHeight, duration: video.duration }); video.removeAttribute('src'); video.load(); };
    video.onerror = () => { clearTimeout(timer); reject(Error('The video result could not be read yet.')); };
    video.src = src;
  });
}

/** The provider's route names, as Artwork's video modes (ai-back fal-spec routes). */
const ROUTE_MODES: Record<string, NonNullable<Model['promptRequiredModes']>[number]> = { text: 'text', image: 'first', firstLast: 'firstLast', keyframes: 'firstLast', reference: 'reference' };
const promptModes = (routes: unknown[]) => [...new Set(routes.flatMap(route => ROUTE_MODES[String(route)] ? [ROUTE_MODES[String(route)]!] : []))];

const strings = (value: unknown): string[] => Array.isArray(value) ? value.map(String) : [];
const num = (value: unknown, fallback: number) => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback;
const modelPools = (kind: MediaKind) => kind === 'image' ? ['text-to-image', 'image-to-image', 'multiple-image-to-image'] : ['text-to-video', 'image-to-video'];
const presentation = (row: Record<string, unknown>) => ({
  provider: String(row.providerLabel || row.provider || 'Other'),
  icon: String(row.icon || row.logo || row.logoUrl || row.iconUrl || row.icon_url || row.logo_url || ''),
  featured: row.isFeatured === true,
  description: typeof row.description === 'string' ? row.description : '',
});
const wire = (request: Request, raw?: Model['raw'], elementPictures?: Map<string, number>) => {
  const pictures = request.references.filter(ref => !ref.element), elements = request.references.flatMap(ref => ref.element ? [ref.element] : []);
  // Tags in the prompt become the words this model reads for them (promptMentions.ts).
  return { prompt: promptForModel(request.prompt, request.references, request.model, elementPictures), type: request.kind, model: request.model, nbVariations: request.quantity,
    modelInputs: request.kind === 'video' && request.video ? videoPayload(raw, request.inputs, request.references, request.video.side) : { ...request.inputs, ...(pictures.length ? { images_urls: pictures.map(ref => ref.url) } : {}) },
    ...(elements.length ? { elementRefs: elements } : {}), ...(request.destination ? { artwork: request.destination } : {}) };
};

/** The model catalogue as last read, per kind, in this browser (a convenience: always asked again). */
const CATALOGUE_KEY = (kind: MediaKind) => `artwork.models.v1.${kind}`;
function readCatalogue(kind: MediaKind) {
  try {
    const kept = JSON.parse(localStorage.getItem(CATALOGUE_KEY(kind)) || 'null') as { at?: number; entries?: unknown } | null;
    return Array.isArray(kept?.entries) && kept.entries.length ? kept.entries as { row: Record<string, unknown>; textOnly: boolean; takesReferences: boolean }[] : null;
  } catch { return null; }
}
function writeCatalogue(kind: MediaKind, entries: unknown[]) {
  try { localStorage.setItem(CATALOGUE_KEY(kind), JSON.stringify({ at: Date.now(), entries })); } catch { /* full or blocked: the next visit fetches again */ }
}

/** Uses the same coordinator as Flow; never calls Fal or writes provider URLs to a document. */
export function gatewayGenerationClient(documentId: string, projectId: string | null): GenerationClient {
  const catalogue = new Map<MediaKind, Promise<Model[]>>();
  const rowsById = new Map<string, Record<string, unknown>>();
  const parameters = new Map<string, Promise<Model>>();
  const recentLists = new Map<string, { at: number; rows: Promise<Model[]> }>();
  type CatalogueEntry = { row: Record<string, unknown>; textOnly: boolean; takesReferences: boolean };
  async function fetchCatalogue(kind: MediaKind): Promise<CatalogueEntry[]> {
    const pools = modelPools(kind);
    const lists = await Promise.all(pools.map(type => gatewayCall<unknown>('ai', 'ai.models.list', { type, ...(kind === 'video' ? { includeAllTasks: true } : {}) }, { method: 'get' })));
    const grouped = new Map<string, CatalogueEntry>();
    lists.forEach((list, index) => {
      for (const raw of Array.isArray(list) ? list : []) {
        const row = kind === 'video' ? videoRaw(raw as Record<string, unknown>) : raw as Record<string, unknown>;
        if (!row.nameId || row.runsOn === 'fabric' || String(row.nameId).startsWith('fabric:')) continue;
        if (kind === 'video' && !makesVideo(row)) continue;
        const id = String(row.nameId), previous = grouped.get(id), textOnly = pools[index]!.startsWith('text-to-');
        const merged = { ...previous?.row, ...row, isFeatured: row.isFeatured === true || previous?.row.isFeatured === true };
        grouped.set(id, { row: merged, textOnly: textOnly || !!previous?.textOnly, takesReferences: !textOnly || !!previous?.takesReferences });
      }
    });
    return [...grouped.values()];
  }
  function toModels(kind: MediaKind, entries: CatalogueEntry[]) {
    for (const { row } of entries) rowsById.set(String(row.nameId), row);
    return entries.map(({ row, textOnly, takesReferences }) => ({ id: String(row.nameId), name: String(row.mainModelName || row.name || row.nameId), kind,
      ...presentation(row), ratios: [], qualities: [], durations: [], modes: [], maxReferences: 0, maxVariations: 1, customSize: false,
      ...(kind === 'video' ? { raw: row } : {}), textOnly, takesReferences }));
  }

  /**
   * How many pictures each tagged element sends NOW: drive-back resolves the same refs, in the
   * same order, for the run a moment later — an element changed since it was added (a look
   * given a picture, a sheet redrawn) would otherwise shift every "image N" after it.
   */
  async function elementPicturesNow(request: Request) {
    const elements = request.references.filter(reference => reference.element);
    if (!elements.length || !request.prompt.includes('@[ref:')) return undefined;
    const resolved = await resolveElements(elements.map(reference => reference.element!));
    return new Map(elements.map((reference, index) => [reference.id, resolved.items.find(item => item.refIndex === index)?.imageIndexes.length ?? 0]));
  }
  async function providerWire(request: Request) {
    const ids = [...new Set(request.references.flatMap(reference => !reference.element && reference.mediaAssetId ? [reference.mediaAssetId] : []))];
    const elementPictures = await elementPicturesNow(request);
    if (!ids.length) return wire(request, rowsById.get(request.model), elementPictures);
    // Canvas snapshots are private media. Resolve bytes through the authenticated
    // media service; the provider cannot use a browser's sign-in or a stale URL.
    const response = await gatewayCall<{ urls?: Record<string, string> }>('media', 'media.getSignedUrls', { mediaAssetIds: ids });
    const references = request.references.map(reference => {
      if (reference.element) return reference;
      const url = reference.mediaAssetId ? response.urls?.[reference.mediaAssetId] : reference.url;
      if (!url || !/^https?:\/\//i.test(url)) throw Error(`A reference ${reference.mediaKind || 'image'} is unavailable. Remove it and add it again.`);
      return { ...reference, url };
    });
    return wire({ ...request, references }, rowsById.get(request.model), elementPictures);
  }
  /** A vectorize answers an SVG; everything else Artwork runs answers a picture. */
  const resultMime = (item: Record<string, unknown>, url: string) => {
    const said = String(item.mimeType || item.mime || '');
    return said.startsWith('image/') ? said : /\.svgz?(\?|#|$)/i.test(url) ? 'image/svg+xml' : 'image/png';
  };
  async function normalize(raw: Record<string, unknown>): Promise<Run> {
    if (!raw.id) throw Error('The generation service did not return a run ID.');
    const kind = raw.type === 'video' ? 'video' : 'image';
    const facts = Array.isArray(raw.results) ? raw.results as Record<string, unknown>[] : [];
    const results: Result[] = [];
    if (raw.status === 'completed') {
      // Keep the provider's ordering, including unresolved slots. Ingestion must finish
      // before a result is attachable; a missing ID is a saving state, not a new upload.
      for (const [i, item] of facts.entries()) {
        if (!item.mediaAssetId || !item.url) return { id: String(raw.id), status: 'saving', results: [] };
        const url = String(item.url);
        let width = num(item.width, 0), height = num(item.height, 0), duration = num(item.durationSeconds, 0);
        // Size only: no CORS read. A CDN copy cached without CORS headers would fail a
        // crossOrigin read forever and the run would never land (storage-cdn trap).
        if (kind === 'video' && (!width || !height || !duration)) {
          const info = await measureClip(url); width = info.width; height = info.height; duration = info.duration;
        } else if (kind === 'image' && (!width || !height) && resultMime(item, url) !== 'image/svg+xml') {
          // (An SVG — Vectorize — is placed as shapes, at the picture's size: nothing to measure,
          // and decoding a large one as a picture can fail and hold the run in "saving" for good.)
          const image = new Image(); image.src = url; await image.decode();
          width = image.naturalWidth; height = image.naturalHeight;
        }
        results.push({ mediaAssetId: String(item.mediaAssetId), ...(item.driveFileId ? { driveFileId: String(item.driveFileId) } : {}), url,
          ...(item.thumbnailUrl ? { thumbnailUrl: String(item.thumbnailUrl) } : {}), width, height,
          mime: kind === 'video' ? 'video/mp4' : resultMime(item, url), name: String(item.name || `Generation ${i + 1}`), ...(duration ? { duration } : {}) });
      }
      if (!facts.length) return { id: String(raw.id), status: 'saving', results: [] };
    }
    return { id: String(raw.id), ...(raw.aiJobId ? { aiJobId: String(raw.aiJobId) } : {}), status: raw.errorCode === 'CANCELLED' ? 'canceled' : String(raw.status),
      results, ...(raw.userMessage ? { userMessage: String(raw.userMessage) } : {}), ...(raw.resultDocument ? { document: raw.resultDocument } : {}) };
  }
  return {
    async library({ kind, query, cursor, signal }) {
      const { rows, cursor: next } = await readReferenceMedia(kind, { search: query, cursor, signal });
      return { references: rows.flatMap(row => {
        if (!row.mediaAssetId || !row.url || !String(row.mimeType || '').startsWith(`${kind}/`)) return [];
        const preview = row.preview as { thumb?: string; medium?: string } | undefined;
        return [{ id: `media:${row.mediaAssetId}`, mediaAssetId: String(row.mediaAssetId), name: String(row.name || 'Reference'), url: String(row.url), thumbnailUrl: preview?.thumb || preview?.medium || (row.previewUrl ? String(row.previewUrl) : undefined),
          width: num(row.width, 0), height: num(row.height, 0), mime: String(row.mimeType), origin: 'upload' as const, mediaKind: kind,
          ...(num(row.durationSeconds ?? row.duration, 0) ? { duration: Number(row.durationSeconds ?? row.duration) } : {}) }];
      }), cursor: next };
    },
    async elements(refs) {
      const resolved = await resolveElements(refs);
      return Promise.all(refs.map(async (element, index) => {
        const item = resolved.items.find(row => row.refIndex === index);
        if (!item) throw Error('An element could not be resolved. Remove it and try again.');
        const first = resolved.images.find(image => image.refIndex === index);
        // Display only. The run resolves IDs again server-side, exactly as Flow does.
        const url = first?.url || elementThumbnail(await getElement(element.elementId));
        return { id: `element:${element.elementId}:${element.scopeId || ''}:${(element.memberIds || []).join(',')}`, name: item.name,
          url, width: 1, height: 1, mime: 'image/png', origin: 'element', element,
          elementKind: item.kind, elementImageCount: item.imageIndexes.length } satisfies Reference;
      }));
    },
    async models(kind: MediaKind, references: boolean) {
      let cached = catalogue.get(kind);
      if (!cached) {
        // The catalogue is kept in this browser: a refresh shows the last list at once and
        // asks again behind it (Tarek, 14 Oct: "why everytime i refresh we need to fetch all of
        // the models all over again"). A list that changed is used from the next open.
        const kept = readCatalogue(kind);
        const fresh = fetchCatalogue(kind).then(entries => { writeCatalogue(kind, entries); return toModels(kind, entries); });
        if (kept) {
          cached = Promise.resolve(toModels(kind, kept));
          fresh.then(models => { catalogue.set(kind, Promise.resolve(models)); }).catch(() => undefined);
        } else cached = fresh.catch(error => { catalogue.delete(kind); throw error; });
        catalogue.set(kind, cached);
      }
      const loaded = await cached as (Model & { textOnly?: boolean; takesReferences?: boolean })[];
      if (kind === 'video') { const first = startingVideoModel(loaded); return first ? [first, ...loaded.filter(model => model.id !== first.id)] : loaded; }
      return loaded.filter(model => references ? model.takesReferences : model.textOnly);
    },
    async recent(kind, references) {
      const scope = `${kind}:${references}`;
      const held = recentLists.get(scope);
      if (held && Date.now() - held.at < 5 * 60_000) return held.rows;
      const rows = (async () => {
        const available = new Map((await this.models(kind, references)).map(model => [model.id, model]));
        const response = await gatewayCall<unknown>('ai', 'ai.models.recent', { types: modelPools(kind).join(','), limit: 10 }, { method: 'get' });
        const raw = Array.isArray(response) ? response : response && typeof response === 'object' && 'data' in response ? (response as { data: unknown }).data : null;
        if (!Array.isArray(raw)) throw Error('Recently used models are unavailable.');
        const seen = new Set<string>();
        return raw.flatMap(row => {
          if (!row || typeof row !== 'object') return [];
          const id = String(row.nameId || row.clientModelName || row.name || '');
          const model = available.get(id);
          if (!model || seen.has(id)) return [];
          seen.add(id);
          return [model];
        });
      })().catch(error => { recentLists.delete(scope); throw error; });
      recentLists.set(scope, { at: Date.now(), rows });
      return rows;
    },
    async params(model) {
      let cached = parameters.get(model.id);
      if (!cached) {
        cached = (async () => {
          const caps = await gatewayCall<Record<string, unknown>>('ai', 'ai.models.supportedParams', { nameId: model.id });
          const row = rowsById.get(model.id) || {};
          const inputs = Array.isArray(row.modelInputs) ? row.modelInputs as Record<string, unknown>[] : Object.values(row.modelInputs || {}).flat() as Record<string, unknown>[];
          const thinking = inputs.find(input => input.reference === 'thinkingLevel');
          const data = thinking?.modelInputData as { values?: unknown } | undefined;
          return { ...model, ratios: strings(caps.ratios), qualities: strings(caps.qualities), durations: strings(caps.durations), modes: strings(caps.modes),
            maxReferences: num(caps.maxReferenceImages, 0), maxVariations: num(caps.maxVariations, 1), maxInputImageBytes: num(caps.maxInputImageBytes, 0),
            customSize: caps.supportsCustomSize === true, customSizeLimits: (caps.customSizeLimits || row.customSizeLimits) as Model['customSizeLimits'],
            imageInputRole: caps.imageInputRole ? String(caps.imageInputRole) : undefined,
            audio: Array.isArray(caps.generate_audio) ? caps.generate_audio.filter((value): value is boolean => typeof value === 'boolean') : [],
            thinkingLevels: caps.supportsThinkingLevel ? strings(data?.values) : [], transparent: caps.supportsTransparentBackground === true,
            durationMaxWhen: Array.isArray(caps.durationMaxWhen) ? caps.durationMaxWhen as Model['durationMaxWhen'] : [],
            ...(Array.isArray(caps.promptRequiredRoutes) ? { promptRequiredModes: promptModes(caps.promptRequiredRoutes) } : {}) };
        })().catch(error => { parameters.delete(model.id); throw error; });
        parameters.set(model.id, cached);
      }
      return cached;
    },
    async quote(request) {
      const result = await gatewayCall<Record<string, unknown>>('nodes', 'nodes.standalone.quote', await providerWire(request));
      const value = result.totalCredits ?? result.creditsRequired ?? result.credits;
      if (value === undefined || !Number.isFinite(Number(value))) throw Error('The generation price is unavailable. Try again.');
      const seconds = Number(result.estimatedSeconds);
      return { credits: Number(value), ...(Number.isFinite(seconds) && seconds > 0 ? { seconds } : {}) };
    },
    async generate(request) { const run = await normalize(await gatewayCall('nodes', 'nodes.standalone.generate', { ...await providerWire(request), source: 'artwork' })); recentLists.clear(); return run; },
    async get(generationId) { return normalize(await gatewayCall('nodes', 'nodes.standalone.get', { generationId })); },
    async cancel(generationId) {
      const response = await gatewayCall<{ cancelled?: boolean; stopped?: boolean }>('nodes', 'nodes.standalone.cancel', { generationId });
      return { cancelled: response.cancelled === true, ...(typeof response.stopped === 'boolean' ? { stopped: response.stopped } : {}) };
    },
    feed: playgroundFeed,
    async reference(blob, name, origin, objectId, onProgress) {
      const file = new File([blob], name, { type: blob.type });
      const picture = blob.type.startsWith('audio/') ? await uploadAudioReference(file, { projectId }, onProgress) : blob.type.startsWith('video/') ? await uploadVideoReference(file, { projectId }, onProgress) : await uploadPicture(documentId, file, origin === 'upload' ? { projectId } : undefined, onProgress);
      return { id: crypto.randomUUID(), name, url: picture.url, width: picture.width, height: picture.height, mime: picture.mime,
        mediaAssetId: picture.mediaAssetId, objectId, origin,
        ...(picture.duration ? { duration: picture.duration } : {}),
        ...(picture.mime.startsWith('audio/') ? { mediaKind: 'audio' as const } : picture.mime.startsWith('video/') ? { mediaKind: 'video' as const } : {}) } satisfies Reference;
    },
  };
}
